/**
 * tenantBranding — pure white-label brand-name resolution (tenant-foundation
 * Task 18). UI-free + side-effect-free so the precedence rules can be unit-tested
 * directly. `BrandingContext` consumes this to overlay the active tenant's own
 * name onto the branding it exposes.
 */

/** The minimal Membership shape this resolver needs (a subset of `MembershipSummary`). */
export interface BrandNameMembership {
  tenantId: string;
  tenantName: string;
}

/**
 * Resolve the effective brand (pageant) name for the active surface.
 *
 * Precedence:
 *   1. The active tenant's own name — the canonical `Tenant.name`, surfaced
 *      client-side as the matching Membership's `tenantName`. This is what makes
 *      the authenticated app read as the active tenant (e.g. "Meridian Pageants").
 *   2. The year-keyed `BrandingConfig.pageantName` (the single-tenant / public
 *      default — Idaho's own site has no active-tenant claim).
 *   3. A neutral fallback.
 *
 * A blank/absent `activeTenantId` (public / pre-auth) skips straight to the
 * fetched/default name, so the public marketing site stays Idaho's.
 */
export function resolveBrandName(
  activeTenantId: string | null | undefined,
  memberships: ReadonlyArray<BrandNameMembership>,
  fetchedPageantName: string | null | undefined,
  fallback = 'Idaho Pageants',
): string {
  const id = typeof activeTenantId === 'string' ? activeTenantId.trim() : '';
  if (id) {
    // Trim BOTH sides. Trimming only the claim was a latent asymmetry: a tenantId
    // carrying stray whitespace would fail to match its own claim and silently fall
    // through to the default brand. Real tenant ids have no whitespace, so this is
    // unreachable today — but a comparison that trims one operand and not the other
    // is the kind of thing that becomes reachable later, and trimming both can never
    // match two genuinely distinct ids.
    const active = memberships.find((m) => m.tenantId?.trim() === id);
    const name = active?.tenantName?.trim();
    if (name) return name;
  }
  const fetched = fetchedPageantName?.trim();
  return fetched && fetched.length > 0 ? fetched : fallback;
}
